import { useCallback, useEffect, useState } from 'react';

import type { IProductOption, IProductOptionGroup } from '@/entities/product/types';

export interface ISelectedOptionGroup {
  optionGroupIdx: string;
  optionGroupTitle: string;
  optionList: IProductOption[];
}

const useProductSelectOptionGroup = (
  optionGroupList: IProductOptionGroup[] = [],
  isExpanded: boolean,
) => {
  const [selectedOptionGroupList, setSelectedOptionGroupList] = useState<ISelectedOptionGroup[]>(
    [],
  );

  const handleResetOptionGroup = useCallback((): void => {
    setSelectedOptionGroupList(
      optionGroupList.map(group => ({
        optionGroupIdx: group.idx,
        optionGroupTitle: group.title,
        optionList: [],
      })),
    );
  }, [optionGroupList]);

  useEffect((): void => {
    if (isExpanded && optionGroupList.length > 0) {
      setSelectedOptionGroupList(prev => {
        // 이미 같은 구조로 초기화되어 있다면 업데이트하지 않음
        if (
          prev.length === optionGroupList.length &&
          prev.every((item, index) => item.optionGroupIdx === optionGroupList[index]?.idx)
        ) {
          return prev;
        }

        return optionGroupList.map(group => ({
          optionGroupIdx: group.idx,
          optionGroupTitle: group.title,
          optionList: [],
        }));
      });
    }
  }, [optionGroupList, isExpanded]);

  const handleSelectOption =
    ({
      optionGroupIndex,
      isMultiSelect,
      option,
    }: {
      optionGroupIndex: number;
      option: IProductOption;
    } & Pick<IProductOptionGroup, 'isMultiSelect'>) =>
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();

      setSelectedOptionGroupList(prev => {
        const newList = [...prev];

        // 그룹이 존재하지 않으면 그대로 반환
        if (optionGroupIndex >= newList.length) {
          return newList;
        }

        const isAlreadySelected = newList[optionGroupIndex].optionList.some(
          selectedOption => selectedOption.idx === option.idx,
        );

        if (isAlreadySelected) {
          // 선택 해제
          newList[optionGroupIndex] = {
            ...newList[optionGroupIndex],
            optionList: newList[optionGroupIndex].optionList.filter(
              selectedOption => selectedOption.idx !== option.idx,
            ),
          };
        } else {
          // 선택 추가
          newList[optionGroupIndex] = {
            ...newList[optionGroupIndex],
            optionList: isMultiSelect
              ? [...newList[optionGroupIndex].optionList, option] // 다중 선택
              : [option], // 단일 선택
          };
        }

        return newList;
      });
    };

  return { selectedOptionGroupList, handleSelectOption, handleResetOptionGroup };
};

export default useProductSelectOptionGroup;
